import React, { useMemo, useState, useEffect } from "react";
import { Input, Typography, Empty, Button } from "antd";
import { SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { renderInlineMarkdown } from "./markdownInline.js";
import { parseDictionary, slugifyTerm } from "./dictionaryUtils.js";

const { Paragraph, Text } = Typography;

// Disciplines-agnostic glossary — pages/bimframework/Dictionary/dictionary.md, authored as a
// flat "- Term\nDefinition paragraph" list (not the "# Heading" convention used elsewhere,
// since this is a running list the user keeps appending to, not a fixed set of fields). Parsed,
// alphabetized, and grouped by first letter here rather than shown in raw file order — a
// glossary reads as a reference tool, not a document to read top-to-bottom.
//
// Each entry's stable id (dict-term-<slug>) is what makes cross-referencing from elsewhere in
// BIM Framework possible (see svgTermMatch.js) — arriving via one of those links passes
// `focusTerm` (scrolls to + briefly highlights that entry) and `returnTo` (shows a "Back to
// <section>" button, since a reader who followed a definition link away from whatever they were
// actually reading should be able to get back to it in one click, not by re-finding it in the
// left nav from scratch).

export default function DictionaryView({ content, focusTerm, returnTo, onReturn }) {
  const [search, setSearch] = useState("");
  const [highlightSlug, setHighlightSlug] = useState(null);

  const entries = useMemo(
    () => parseDictionary(content?.description).sort((a, b) => a.term.localeCompare(b.term)),
    [content]
  );

  useEffect(() => {
    if (!focusTerm) return;
    const el = document.getElementById(`dict-term-${focusTerm}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightSlug(focusTerm);
    const t = setTimeout(() => setHighlightSlug(null), 2200);
    return () => clearTimeout(t);
  }, [focusTerm, entries]);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? entries.filter((e) => e.term.toLowerCase().includes(term) || e.definition.toLowerCase().includes(term))
    : entries;

  const groups = [];
  for (const e of filtered) {
    const letter = e.term[0]?.toUpperCase() || "#";
    let group = groups[groups.length - 1];
    if (!group || group.letter !== letter) { group = { letter, items: [] }; groups.push(group); }
    group.items.push(e);
  }

  if (entries.length === 0) {
    return <Empty description="No definitions yet — add entries to Dictionary/dictionary.md" style={{ marginTop: 40 }} />;
  }

  return (
    <div>
      <style>{`
        .dict-jump { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 0 24px; }
        .dict-jump a {
          display: inline-flex; align-items: center; justify-content: center;
          width: 26px; height: 26px; border-radius: 6px;
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.55); font-family: 'DM Mono', monospace; font-size: 12px;
          text-decoration: none; transition: background 120ms ease, color 120ms ease;
        }
        .dict-jump a:hover { background: rgba(79,156,249,0.16); color: #7ab8ff; }
        .dict-letter {
          font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 800; font-size: 13px;
          color: #4f9cf9; letter-spacing: 0.08em; margin: 28px 0 4px;
          padding-bottom: 6px; border-bottom: 1px solid rgba(79,156,249,0.25);
          scroll-margin-top: 12px;
        }
        .dict-letter:first-of-type { margin-top: 0; }
        .dict-entry {
          padding: 14px 10px; margin: 0 -10px; border-radius: 8px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          scroll-margin-top: 12px; transition: background 400ms ease;
        }
        .dict-entry:last-child { border-bottom: none; }
        .dict-entry.highlight { background: rgba(79,156,249,0.14); }
        .dict-term {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14.5px;
          color: #fff; display: block; margin-bottom: 4px;
        }
        .dict-def { margin: 0 !important; color: rgba(255,255,255,0.72); font-size: 13.5px; line-height: 1.65; }
      `}</style>

      {returnTo && (
        <Button
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={onReturn}
          style={{ marginBottom: 16 }}
        >
          Back to {returnTo.label}
        </Button>
      )}

      <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 16, maxWidth: 720 }}>
        Shared definitions used across this BIM Framework — {entries.length} term{entries.length !== 1 ? "s" : ""} so far.
      </Paragraph>

      <Input
        placeholder="Search definitions…"
        prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.3)" }} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ maxWidth: 320, marginBottom: 8 }}
      />

      {!term && groups.length > 1 && (
        <div className="dict-jump">
          {groups.map((g) => <a key={g.letter} href={`#dict-${g.letter}`}>{g.letter}</a>)}
        </div>
      )}

      {filtered.length === 0 ? (
        <Empty description="No matching definitions" style={{ marginTop: 32 }} />
      ) : (
        groups.map((g) => (
          <div key={g.letter}>
            <div id={`dict-${g.letter}`} className="dict-letter">{g.letter}</div>
            {g.items.map((e) => {
              const slug = slugifyTerm(e.term);
              return (
                <div
                  key={e.term}
                  id={`dict-term-${slug}`}
                  className={`dict-entry${highlightSlug === slug ? " highlight" : ""}`}
                >
                  <Text className="dict-term">{renderInlineMarkdown(e.term)}</Text>
                  <Paragraph className="dict-def">{renderInlineMarkdown(e.definition)}</Paragraph>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
